import { useMemo } from "react";
import { queries } from "../../shared/queries";
import type { SpaceMode } from "../../shared/spaceMode";
import { getWritableScopeIds } from "../lib/getWritableScopeIds";
import { useQuery } from "./useQuery";
import { useSpaceDisplayName } from "./useSpaceDisplayName";

export type MoveTarget = {
  id: string;
  name: string;
  mode: SpaceMode;
  disabled?: boolean;
  disabledReason?: string;
};

export function useMoveTargets(): MoveTarget[] {
  const [spaces] = useQuery(queries.spaces({ settingsView: true }));

  const [myGrants] = useQuery(queries.currentUserGrants());

  const displaySpaceName = useSpaceDisplayName();

  const [personalSpaceOwners] = useQuery(queries.personalSpaceOwners());
  const ownerBySpaceId = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of personalSpaceOwners) {
      const name = s.roleGrantsAsScope?.[0]?.user?.name;
      if (name) map.set(s.id, name);
    }
    return map;
  }, [personalSpaceOwners]);

  const spaceIds = useMemo(() => spaces.map((s) => s.id), [spaces]);
  const writableSpaceIds = useMemo(
    () => getWritableScopeIds(myGrants ?? [], spaceIds, "space:write"),
    [myGrants, spaceIds]
  );

  return useMemo(
    () =>
      spaces.map((s) => {
        const name = displaySpaceName(s, ownerBySpaceId.get(s.id));
        if (!writableSpaceIds.has(s.id)) {
          return {
            id: s.id,
            mode: s.mode,
            name,
            disabled: true,
            disabledReason: "You don't have write access to this space",
          };
        }
        return { id: s.id, mode: s.mode, name };
      }),
    [spaces, displaySpaceName, writableSpaceIds, ownerBySpaceId]
  );
}
